import { useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AppShell } from '../../app/AppShell'
import { api } from '../../api/client'
import {
  Button,
  Card,
  CollapsibleCard,
  IconButton,
  OrderButtons,
  SectionLabel,
} from '../../design/components'
import { GenerationCard } from '../jobs/GenerationCard'
import { useGenerationJob } from '../jobs/useGenerationJob'
import type { GenerationJob } from '../jobs/types'
import { ComposerHelp } from './ComposerHelp'
import { ToolBar, ToolPanel, type ToolName } from './ComposerTools'
import { DraftResults } from './DraftResults'
import { composerKeys, useDraftActions, useDrafts } from './queries'
import {
  blockSummary,
  composerPath,
  DEFAULT_SETTINGS,
  moveBlock,
  toRequestBlocks,
  type ComposerBlock,
  type ComposerOwner,
  type ComposerSettings,
  type Resolution,
  type ThinkingLevel,
} from './types'
import styles from './composer.module.css'

export interface ComposerScreenProps {
  owner: ComposerOwner
  /** The character's or location's name. Heads the screen and names the blocks tools produce. */
  subjectName: string
  subtitle: string
  /** Whether this caller may spend model time here — owner for a character, GM for a place. */
  canCompose: boolean
  /**
   * The one piece of advice that is consequential rather than merely useful, shown on the prompt
   * card itself. The rest lives behind the help icon; this is the part that must be read.
   */
  hint?: ReactNode
  /** Extra fields folded into the Settings card — the location composer's image type. */
  extraSettings?: ReactNode
  /** Prefixed to the collapsed Settings summary, so the type is visible without opening it. */
  settingsSummaryPrefix?: string
  /** Fields merged into the compose request beyond blocks and settings. */
  requestExtras?: Record<string, unknown>
  /** What the Generate card says it is about to do. */
  generateWhat: string
  generateAction: string
  /** What a downloaded result is called. */
  downloadName: string
  /** The way back — a gallery, usually. */
  back: ReactNode
}

/**
 * The workbench, for either subject.
 *
 * A prompt here is a **list**, not a sentence: art direction, a reference picture, the subject's
 * own words, a note about what to change — in the order an artist would say them. That is the
 * shape the Blazor composers have, and it is the shape that makes a second attempt a revision of
 * the first rather than another roll of the dice.
 *
 * Two things distinguish this from the one-button generators on the gallery screens, and both are
 * deliberate:
 *
 * - **Everything in the prompt is visible and yours.** Nothing is read from the sheet or the
 *   location record behind your back. If the description should be in there, you add it — and
 *   edit it first.
 * - **Results are drafts.** They are not in the gallery, and saving one is a separate tap. You
 *   generate three, two are wrong, and only the third is a picture this subject has.
 */
export function ComposerScreen({
  owner,
  subjectName,
  subtitle,
  canCompose,
  hint,
  extraSettings,
  settingsSummaryPrefix,
  requestExtras,
  generateWhat,
  generateAction,
  downloadName,
  back,
}: ComposerScreenProps) {
  const queryClient = useQueryClient()

  const [blocks, setBlocks] = useState<ComposerBlock[]>([])
  const [tool, setTool] = useState<ToolName | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const [settings, setSettings] = useState<ComposerSettings>(DEFAULT_SETTINGS)

  const drafts = useDrafts(owner, canCompose)
  const actions = useDraftActions(owner)

  // Held in a ref because `start` is called by the job hook from a closure created on an
  // earlier render — reading state there would send the prompt as it was when the hook was
  // built, which is the empty one. Same reason the comics composer keeps one.
  const request = useRef<Record<string, unknown>>({})
  request.current = { blocks: toRequestBlocks(blocks), settings, ...requestExtras }

  const job = useGenerationJob({
    start: () => api.post<GenerationJob>(`${composerPath(owner)}/compose`, request.current),
    // The finished job carries a draft id and nothing else; the picture itself is in the draft
    // list, so that is what gets refetched.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: composerKeys.drafts(owner) })
    },
  })

  const add = (added: ComposerBlock[]) => {
    setBlocks((current) => [...current, ...added])
    setTool(null)
  }

  const summary = `${settingsSummaryPrefix === undefined ? '' : `${settingsSummaryPrefix} · `}${settings.resolution} · ${settings.thinkingLevel.toLowerCase()}`

  return (
    <AppShell title={subjectName} subtitle={subtitle}>
      <Card
        variant="magic"
        label="Prompt"
        hint={blocks.length === 1 ? '1 block' : `${blocks.length} blocks`}
        /* The controls here are obvious and the method is not — that order matters, that a
           reference beats a paragraph, that Refine is how you converge. One icon, read once. */
        action={
          <IconButton
            label="How to use this screen"
            icon="help"
            onClick={() => setHelpOpen(true)}
          />
        }
      >
        {hint}

        {blocks.length === 0 ? (
          <p className={styles.empty}>
            Nothing in the prompt yet. Add text, a reference picture, or this subject&apos;s own
            words.
          </p>
        ) : (
          blocks.map((block, index) => (
            <PromptBlockRow
              key={block.id}
              block={block}
              index={index}
              total={blocks.length}
              onMove={(to) => setBlocks((current) => moveBlock(current, index, to))}
              onEdit={(text) =>
                setBlocks((current) =>
                  current.map((entry) =>
                    entry.id === block.id && entry.kind === 'Text' ? { ...entry, text } : entry,
                  ),
                )
              }
              onRemove={() =>
                setBlocks((current) => current.filter((entry) => entry.id !== block.id))
              }
            />
          ))
        )}

        {canCompose && (
          <>
            <ToolBar
              owner={owner}
              open={tool}
              onOpen={setTool}
              hasDrafts={(drafts.data ?? []).length > 0}
            />

            {tool !== null && (
              <ToolPanel
                tool={tool}
                owner={owner}
                subjectName={subjectName}
                drafts={drafts.data ?? []}
                onAdd={add}
                onClose={() => setTool(null)}
              />
            )}
          </>
        )}
      </Card>

      <CollapsibleCard label="Settings" summary={summary}>
        {extraSettings}
        <SettingsFields settings={settings} onChange={setSettings} />
      </CollapsibleCard>

      {/* No prompt, no card. The API refuses an empty one, and a button whose only outcome is
          a refusal is worse than no button — the Prompt card above already says what to do.

          A job in flight keeps the card regardless: emptying the prompt while one is running
          must not take away the progress line and the Cancel button along with it. */}
      <GenerationCard
        label="Generate"
        what={generateWhat}
        action={generateAction}
        canGenerate={canCompose && (blocks.length > 0 || job.isRunning || job.job !== null)}
        job={job}
      />

      <DraftResults
        drafts={drafts.data ?? []}
        actions={actions}
        onReuse={add}
        downloadName={downloadName}
      />

      {helpOpen && <ComposerHelp owner={owner} onClose={() => setHelpOpen(false)} />}

      {back}
    </AppShell>
  )
}

function PromptBlockRow({
  block,
  index,
  total,
  onMove,
  onEdit,
  onRemove,
}: {
  block: ComposerBlock
  index: number
  total: number
  onMove: (to: number) => void
  onEdit: (text: string) => void
  onRemove: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(block.kind === 'Text' ? block.text : '')

  const summary = blockSummary(block)

  return (
    <div className={styles.block}>
      {/* A disclosure, because a prompt is a list of things that are individually long — a
          profile snapshot alone is forty lines. Collapsed, each block is one line that says
          which one it is; the shape of the whole prompt fits on a phone screen again.

          `<details>` rather than a button and a piece of state: the open/closed announcement,
          keyboard operation and find-in-page all come from the platform. The controls sit
          outside it, because a button nested inside a `<summary>` is a nested-interactive
          failure that screen readers may never announce. */}
      <details className={styles.blockDetails}>
        <summary className={styles.blockSummary}>
          {/* The position. The order is the meaning of this list, and a reader who has just
              moved a block needs to see where it landed. */}
          <span className={styles.position} aria-hidden="true">
            {index + 1}
          </span>
          <span className={styles.blockTitle}>{summary}</span>
          <span className={styles.rowMeta}>{block.kind === 'Image' ? 'Image' : 'Text'}</span>
          <svg className={styles.chevron} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path
              d="M4 6l4 4 4-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </summary>

        <div className={styles.blockBody}>
          {block.kind === 'Image' ? (
            <img className={styles.blockThumb} src={block.preview} alt="" loading="lazy" />
          ) : editing ? (
            <form
              className={styles.inlineForm}
              onSubmit={(event) => {
                event.preventDefault()
                onEdit(draft.trim())
                setEditing(false)
              }}
            >
              <label className={styles.field}>
                <SectionLabel tone="muted" micro>
                  Text
                </SectionLabel>
                {/* The editor is the read view with a caret in it — same font, same size,
                    same line breaks, and tall enough to hold what is actually there. A
                    forty-line profile in a ten-line box is edited by scrolling blind, which
                    is exactly when someone deletes the wrong paragraph. */}
                <textarea
                  className={styles.textareaProse}
                  value={draft}
                  autoFocus
                  rows={editorRows(draft)}
                  maxLength={20000}
                  onChange={(event) => setDraft(event.target.value)}
                />
              </label>
              <div className={styles.actions}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDraft(block.text)
                    setEditing(false)
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={draft.trim() === ''}>
                  Save
                </Button>
              </div>
            </form>
          ) : (
            <>
              <p className={styles.blockText}>{block.text}</p>
              <div className={styles.actions}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDraft(block.text)
                    setEditing(true)
                  }}
                >
                  Edit
                </Button>
              </div>
            </>
          )}
        </div>
      </details>

      <span className={styles.blockControls}>
        <OrderButtons label={`block ${index + 1}`} index={index} total={total} onMove={onMove} />
        <IconButton label={`Remove block ${index + 1}`} icon="delete" onClick={onRemove} />
      </span>
    </div>
  )
}

/**
 * The model's knobs.
 *
 * Collapsed by default, because the defaults are the right answer almost always and a phone
 * screen is better spent on the prompt. They are here at all because the Blazor composers have
 * them and because resolution genuinely matters when a picture is destined for print — which a
 * battle map, drawn A4 landscape, very often is.
 */
function SettingsFields({
  settings,
  onChange,
}: {
  settings: ComposerSettings
  onChange: (settings: ComposerSettings) => void
}) {
  return (
    <>
      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Resolution
        </SectionLabel>
        <select
          className={styles.select}
          value={settings.resolution}
          onChange={(event) =>
            onChange({ ...settings, resolution: event.target.value as Resolution })
          }
        >
          <option value="512">512</option>
          <option value="1K">1K</option>
          <option value="2K">2K</option>
          <option value="4K">4K</option>
        </select>
      </label>

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Temperature — {settings.temperature.toFixed(1)}
        </SectionLabel>
        <input
          className={styles.range}
          type="range"
          min={0}
          max={1}
          step={0.1}
          value={settings.temperature}
          onChange={(event) => onChange({ ...settings, temperature: Number(event.target.value) })}
        />
        <p className={styles.note}>Higher is more inventive; lower follows the prompt closer.</p>
      </label>

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Thinking
        </SectionLabel>
        <select
          className={styles.select}
          value={settings.thinkingLevel}
          onChange={(event) =>
            onChange({ ...settings, thinkingLevel: event.target.value as ThinkingLevel })
          }
        >
          <option value="MINIMAL">Minimal</option>
          <option value="HIGH">High</option>
        </select>
        <p className={styles.note}>High takes longer and reasons harder about a busy prompt.</p>
      </label>
    </>
  )
}

/**
 * How tall the editor should be for this text.
 *
 * Bounded at both ends: never so short that a profile is edited through a slot, never so tall
 * that it pushes its own Save button off a phone screen. Between those it matches the content,
 * which is what makes editing feel like editing the thing rather than a copy of it.
 */
function editorRows(text: string): number {
  return Math.min(24, Math.max(6, text.split('\n').length + 1))
}
